import React from 'react';
import { DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Dropdown, Space, Typography, Row, Col } from 'antd';

import {
  orderByTranslation,
  orderDirectionTranslation,
  orderBy,
  orderDirection,
  sectorTranslation,
  stageTranslation,
  renderStageLabel,
  maintenanceTypeFilter,
  renderMaintenanceTypeFilterLabel,
} from 'src/utils/constants/tasks';
import { toArray } from 'src/utils';

import { IProps } from './filter.type';
import { Wrapper } from './filter.style';
import { IReduxState } from 'src/store';
import { connect, useDispatch, useSelector } from 'react-redux';
import { ItemType } from 'antd/es/menu/hooks/useItems';

const itemsOrderBy: MenuProps['items'] = orderBy.map((item) => ({
  key: item.value,
  label: item.label,
}));

const itemsOrderDirection: MenuProps['items'] = orderDirection.map((item) => ({
  key: item.value,
  label: item.label,
}));

const FilterComponent: React.FC<IProps> = (props) => {
  const { filter, handleReload } = props;

  const dispatch = useDispatch();

  const sectors = useSelector((state: IReduxState) => state.sectors.all) || [];

  const itemsSectors: MenuProps['items'] = [
    {
      key: '0',
      label: 'Todos os Setores',
    },
    ...toArray<any>(sectors, 'Setores').map((sector) => ({
      key: sector.id.toString(),
      label: sector.name,
    })),
  ];

  const itemsStages: MenuProps['items'] = [
    {
      key: 'all',
      label: 'Todos os Estágios',
    },
    ...stageTranslation.map((stage) => ({
      key: stage.value.toString(),
      label: stage.label,
    })),
  ];

  const itemsMaintenanceType: MenuProps['items'] = maintenanceTypeFilter.map(
    (item) => ({
      key: String(item.value),
      label: item.label,
    })
  );

  const handleFilter = (
    e: any,
    type: 'orderBy' | 'orderDirection' | 'sector' | 'stage' | 'maintenanceType'
  ) => {
    switch (type) {
      case 'orderBy':
        dispatch({
          type: 'UPDATE_TASKS_FILTER',
          payload: { ...filter, orderBy: e.key },
        });
        return handleReload();

      case 'orderDirection':
        dispatch({
          type: 'UPDATE_TASKS_FILTER',
          payload: { ...filter, orderDirection: e.key },
        });
        return handleReload();

      case 'sector':
        dispatch({
          type: 'UPDATE_TASKS_FILTER',
          payload: {
            ...filter,
            sectorId: e.key === '0' ? undefined : parseInt(e.key, 10),
          },
        });
        return handleReload();

      case 'stage':
        dispatch({
          type: 'UPDATE_TASKS_FILTER',
          payload: {
            ...filter,
            stage: e.key === 'all' ? undefined : parseInt(e.key, 10),
          },
        });
        return handleReload();

      case 'maintenanceType':
        dispatch({
          type: 'UPDATE_TASKS_FILTER',
          payload: {
            ...filter,
            maintenanceType:
              e.key === 'all' ? undefined : parseInt(e.key, 10),
          },
        });
        return handleReload();

      default:
        return handleReload();
    }
  };

  const renderFilterOptions = (
    label: string | any,
    items: ItemType[],
    type: 'orderBy' | 'orderDirection' | 'sector' | 'stage' | 'maintenanceType'
  ) => {
    return (
      <Dropdown
        className="my-2 p-2 d-flex justify-content-center"
        menu={{
          items,
          selectable: true,
          className: 'limit-dropdown',
          onSelect: (e) => handleFilter(e, type),
        }}
      >
        <Typography.Link
          style={{
            backgroundColor: '#eee',
            borderRadius: 2,
            color: '#4174b4',
            fontWeight: 600,
          }}
        >
          <Space>
            {label}
            <DownOutlined />
          </Space>
        </Typography.Link>
      </Dropdown>
    );
  };

  return (
    <Wrapper>
      <Row gutter={[16, 8]} wrap className="board-filters-row">
        <Col xs={24} sm={12} md={8} lg="auto">
          <p>
            Filtrando as <b>ordens de serviço</b> por:
          </p>
          {renderFilterOptions(
            orderByTranslation[filter.orderBy],
            itemsOrderBy,
            'orderBy'
          )}
        </Col>
        <Col xs={24} sm={12} md={8} lg="auto">
          <p>
            em <b>ordem:</b>
          </p>

          {renderFilterOptions(
            orderDirectionTranslation[filter.orderDirection],
            itemsOrderDirection,
            'orderDirection'
          )}
        </Col>
        <Col xs={24} sm={12} md={8} lg="auto">
          <p>
            do <b>setor:</b>
          </p>

          {renderFilterOptions(
            sectorTranslation(
              filter.sectorId
                ? sectors.find((s) => s.id === filter?.sectorId)?.name ||
                    'Setor'
                : 'Todos os Setores'
            ),
            itemsSectors,
            'sector'
          )}
        </Col>
        <Col xs={24} sm={12} md={8} lg="auto">
          <p>
            no <b>estágio:</b>
          </p>

          {renderFilterOptions(
            filter.stage !== undefined
              ? renderStageLabel(
                  filter.stage,
                  stageTranslation.find((s) => s.value === filter.stage)
                    ?.label || 'Estágio'
                )
              : renderStageLabel(-1, 'Todos os Estágios'),
            itemsStages,
            'stage'
          )}
        </Col>
        <Col xs={24} sm={12} md={8} lg="auto">
          <p>
            do <b>tipo:</b>
          </p>

          {renderFilterOptions(
            renderMaintenanceTypeFilterLabel(
              filter.maintenanceType,
              filter.maintenanceType !== undefined
                ? maintenanceTypeFilter.find(
                    (item) => item.value === filter.maintenanceType
                  )?.label || 'Tipo'
                : 'Todas'
            ),
            itemsMaintenanceType,
            'maintenanceType'
          )}
        </Col>
      </Row>
    </Wrapper>
  );
};

const mapStateToProps = (state: IReduxState) => ({
  filter: state.tasks.filter,
});

export default connect(mapStateToProps)(FilterComponent);
